import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Landmark, 
  Plus, 
  User,
  CreditCard,
  ArrowUpCircle,
  ArrowDownCircle,
  Wallet,
  ChevronDown,
  ChevronUp,
  Copy,
  Ban,
  CheckCircle,
  AlertTriangle,
  Building,
  Briefcase,
  Euro
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : 'https://nova-network-2-0-backend.onrender.com/api';

const Banca = () => {
  const { user, hasPermesso, isSindacatoAdmin } = useAuth();
  const [conti, setConti] = useState([]);
  const [mioiConti, setMioiConti] = useState([]);
  const [contiAziende, setContiAziende] = useState([]);
  const [contiPartiteIVA, setContiPartiteIVA] = useState([]);
  const [users, setUsers] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [partiteIVA, setPartiteIVA] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [movimentoDialogOpen, setMovimentoDialogOpen] = useState(false);
  const [selectedConto, setSelectedConto] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [form, setForm] = useState({
    titolare_id: '',
    tipo_conto: 'CORRENTE',
    saldo_iniziale: 0
  });
  const [movimentoForm, setMovimentoForm] = useState({
    tipo: 'DEPOSITO',
    importo: '',
    causale: ''
  });

  const canGestisci = () => isSindacatoAdmin() || hasPermesso('gestisci_conti');
  const canSospendi = () => isSindacatoAdmin() || hasPermesso('gestisci_conti');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const promises = [axios.get(`${API_URL}/banca/conti/miei`)];
      
      if (canGestisci()) {
        promises.push(axios.get(`${API_URL}/banca/conti`));
        promises.push(axios.get(`${API_URL}/users`));
        promises.push(axios.get(`${API_URL}/aziende`));
        promises.push(axios.get(`${API_URL}/partite-iva`).catch(() => ({ data: [] })));
      }
      
      const results = await Promise.all(promises);
      setMioiConti(results[0].data);
      
      if (results[1]) {
        const tuttiConti = results[1].data;
        // Separa conti cittadini da conti aziende/partite IVA
        const contiCittadini = tuttiConti.filter(c => 
          c.titolare_tipo !== 'azienda' && c.titolare_tipo !== 'partita_iva'
        );
        const contiAz = tuttiConti.filter(c => c.titolare_tipo === 'azienda');
        const contiPIVA = tuttiConti.filter(c => c.titolare_tipo === 'partita_iva');
        
        setConti(contiCittadini);
        setContiAziende(contiAz);
        setContiPartiteIVA(contiPIVA);
      }
      if (results[2]) setUsers(results[2].data);
      if (results[3]) setAziende(results[3].data);
      if (results[4]) setPartiteIVA(results[4].data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConto = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/banca/conti`, {
        ...form,
        saldo_iniziale: parseFloat(form.saldo_iniziale) || 0
      });
      toast.success('Conto creato con successo');
      setDialogOpen(false);
      setForm({ titolare_id: '', tipo_conto: 'CORRENTE', saldo_iniziale: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleMovimento = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/banca/conti/${selectedConto.id}/movimento`, {
        conto_id: selectedConto.id,
        tipo: movimentoForm.tipo,
        importo: parseFloat(movimentoForm.importo),
        causale: movimentoForm.causale
      });
      toast.success('Movimento registrato');
      setMovimentoDialogOpen(false);
      setMovimentoForm({ tipo: 'DEPOSITO', importo: '', causale: '' });
      setSelectedConto(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel movimento');
    }
  };

  const handleToggleCarta = async (contoId, attiva) => {
    try {
      await axios.patch(`${API_URL}/banca/conti/${contoId}/carta?attiva=${attiva}`);
      toast.success(attiva ? 'Carta attivata' : 'Carta disattivata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleToggleSospensione = async (contoId, sospendi) => {
    try {
      const nuovoStato = sospendi ? 'SOSPESO' : 'ATTIVO';
      await axios.patch(`${API_URL}/banca/conti/${contoId}/stato?stato=${nuovoStato}`);
      toast.success(sospendi ? 'Conto sospeso' : 'Conto riattivato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const toggleExpand = (id) => {
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const copyIban = (iban) => {
    navigator.clipboard.writeText(iban);
    toast.success('IBAN copiato');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'SOSPESO':
        return <Badge className="bg-red-100 text-red-700 border-red-200"><Ban className="w-3 h-3 mr-1" />Sospeso</Badge>;
      case 'CHIUSO':
        return <Badge variant="secondary">Chiuso</Badge>;
      default:
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle className="w-3 h-3 mr-1" />Attivo</Badge>;
    }
  };

  const renderContoCard = (conto, tipo = 'cittadino') => {
    const iconBg = tipo === 'azienda' ? 'bg-purple-100' : tipo === 'partita_iva' ? 'bg-emerald-100' : 'bg-amber-100';
    const iconColor = tipo === 'azienda' ? 'text-purple-600' : tipo === 'partita_iva' ? 'text-emerald-600' : 'text-amber-600';
    const Icon = tipo === 'azienda' ? Building : tipo === 'partita_iva' ? Briefcase : Landmark;
    
    return (
      <Card key={conto.id} className={`hover:shadow-lg transition-all duration-300 ${conto.stato === 'SOSPESO' ? 'border-red-200 bg-red-50/50' : 'border-slate-200'}`}>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm ${
                conto.stato === 'SOSPESO' 
                  ? 'bg-red-100' 
                  : iconBg
              }`}>
                {conto.stato === 'SOSPESO' ? (
                  <Ban className="w-7 h-7 text-red-600" />
                ) : (
                  <Icon className={`w-7 h-7 ${iconColor}`} />
                )}
              </div>
              <div>
                <CardTitle className="text-lg text-slate-800">{conto.titolare_nome}</CardTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-slate-500">
                    {tipo === 'azienda' ? 'Azienda' : tipo === 'partita_iva' ? 'P.IVA' : conto.tipo_conto || 'Corrente'}
                  </Badge>
                  {getStatoBadge(conto.stato)}
                  {conto.carta_attiva && (
                    <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                      <CreditCard className="w-3 h-3 mr-1" />
                      Carta
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-3xl font-bold ${(conto.saldo || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrency(conto.saldo)}
              </p>
              <button
                onClick={() => toggleExpand(conto.id)}
                className="text-slate-400 hover:text-amber-500 transition-colors mt-1 p-1"
              >
                {expandedCards[conto.id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* IBAN */}
          {conto.iban && (
            <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl mb-4">
              <span className="text-xs text-slate-500 font-medium">IBAN:</span>
              <span className="font-mono text-sm text-slate-700 flex-1">{conto.iban}</span>
              <button onClick={() => copyIban(conto.iban)} className="text-slate-400 hover:text-amber-500 transition-colors p-1">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Avviso se sospeso */}
          {conto.stato === 'SOSPESO' && (
            <div className="flex items-center gap-2 bg-red-100 border border-red-200 p-3 rounded-xl mb-4">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="text-sm text-red-700 font-medium">Conto sospeso - Operazioni bloccate</span>
            </div>
          )}

          {/* Movimenti (espandibili) */}
          {expandedCards[conto.id] && (
            <div className="border-t border-slate-100 pt-4 mt-4">
              <h4 className="font-semibold text-slate-700 mb-3">Ultimi Movimenti</h4>
              {conto.movimenti?.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {conto.movimenti.slice().reverse().slice(0, 10).map((mov, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-50 p-3 rounded-xl">
                      <div className="flex items-center gap-3">
                        {mov.tipo === 'DEPOSITO' || mov.tipo === 'ACCREDITO' ? (
                          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                            <ArrowDownCircle className="w-4 h-4 text-emerald-600" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                            <ArrowUpCircle className="w-4 h-4 text-red-600" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-slate-700">{mov.causale}</p>
                          <p className="text-xs text-slate-400">{new Date(mov.created_at).toLocaleDateString('it-IT')}</p>
                        </div>
                      </div>
                      <span className={`font-semibold ${mov.tipo === 'DEPOSITO' || mov.tipo === 'ACCREDITO' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {mov.tipo === 'DEPOSITO' || mov.tipo === 'ACCREDITO' ? '+' : '-'}{formatCurrency(mov.importo)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">Nessun movimento</p>
              )}
            </div>
          )}

          {/* Azioni */}
          {canGestisci() && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
              <Button
                size="sm"
                onClick={() => {
                  setSelectedConto(conto);
                  setMovimentoDialogOpen(true);
                }}
                disabled={conto.stato === 'SOSPESO'}
                className="bg-amber-500 hover:bg-amber-600 text-white shadow-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Movimento
              </Button>
              {tipo === 'cittadino' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleToggleCarta(conto.id, !conto.carta_attiva)}
                  disabled={conto.stato === 'SOSPESO'}
                  className="border-slate-200"
                >
                  <CreditCard className="w-4 h-4 mr-1" />
                  {conto.carta_attiva ? 'Disattiva Carta' : 'Attiva Carta'}
                </Button>
              )}
              
              {canSospendi() && (
                <Button
                  size="sm"
                  variant={conto.stato === 'SOSPESO' ? 'default' : 'destructive'}
                  onClick={() => handleToggleSospensione(conto.id, conto.stato !== 'SOSPESO')}
                  className={conto.stato === 'SOSPESO' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
                >
                  {conto.stato === 'SOSPESO' ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Riattiva
                    </>
                  ) : (
                    <>
                      <Ban className="w-4 h-4 mr-1" />
                      Sospendi
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Calcola totali
  const totaleCittadini = conti.reduce((sum, c) => sum + (c.saldo || 0), 0);
  const totaleAziende = contiAziende.reduce((sum, c) => sum + (c.saldo || 0), 0);
  const totalePartiteIVA = contiPartiteIVA.reduce((sum, c) => sum + (c.saldo || 0), 0);
  const totaleGenerale = totaleCittadini + totaleAziende + totalePartiteIVA;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-200 border-t-amber-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="banca-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center shadow-sm">
            <Landmark className="w-7 h-7 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Gestione Banca</h1>
            <p className="text-slate-500">Conti correnti e operazioni bancarie</p>
          </div>
        </div>
        {canGestisci() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="nuovo-conto-btn" className="bg-amber-500 hover:bg-amber-600 text-white shadow-sm">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Conto Cittadino
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Apri Nuovo Conto Cittadino</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateConto} className="space-y-4">
                <div className="space-y-2">
                  <Label>Titolare</Label>
                  <select
                    className="w-full border border-slate-200 rounded-xl p-3 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                    value={form.titolare_id}
                    onChange={(e) => setForm({...form, titolare_id: e.target.value})}
                    required
                  >
                    <option value="">Seleziona titolare...</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>@{u.username} - {u.nome} {u.cognome}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo Conto</Label>
                  <select
                    className="w-full border border-slate-200 rounded-xl p-3 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                    value={form.tipo_conto}
                    onChange={(e) => setForm({...form, tipo_conto: e.target.value})}
                  >
                    <option value="CORRENTE">Conto Corrente</option>
                    <option value="RISPARMIO">Conto Risparmio</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Saldo Iniziale (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.saldo_iniziale}
                    onChange={(e) => setForm({...form, saldo_iniziale: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-600">Crea Conto</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Statistiche Generali */}
      {canGestisci() && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-amber-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">{formatCurrency(totaleGenerale)}</p>
              <p className="text-sm text-amber-600">Totale Generale</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(totaleCittadini)}</p>
              <p className="text-sm text-blue-600">Conti Cittadini ({conti.length})</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-700">{formatCurrency(totaleAziende)}</p>
              <p className="text-sm text-purple-600">Conti Aziende ({contiAziende.length})</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalePartiteIVA)}</p>
              <p className="text-sm text-emerald-600">Conti P.IVA ({contiPartiteIVA.length})</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* I Miei Conti */}
      {mioiConti.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-amber-500" />
            I Miei Conti
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {mioiConti.map(conto => renderContoCard(conto, 'cittadino'))}
          </div>
        </div>
      )}

      {/* Tabs per Tutti i Conti */}
      {canGestisci() && (
        <Tabs defaultValue="cittadini" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="cittadini" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Cittadini ({conti.length})
            </TabsTrigger>
            <TabsTrigger value="aziende" className="flex items-center gap-2">
              <Building className="w-4 h-4" />
              Aziende ({contiAziende.length})
            </TabsTrigger>
            <TabsTrigger value="partite_iva" className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Partite IVA ({contiPartiteIVA.length})
            </TabsTrigger>
          </TabsList>

          {/* Tab Conti Cittadini */}
          <TabsContent value="cittadini">
            <div>
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-500" />
                Conti Correnti Cittadini
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Gli stipendi vengono automaticamente accreditati su questi conti
              </p>
              {conti.length === 0 ? (
                <Card className="border-dashed border-2 border-slate-200">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                      <User className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500">Nessun conto cittadino registrato</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {conti.map(conto => renderContoCard(conto, 'cittadino'))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Tab Conti Aziende */}
          <TabsContent value="aziende">
            <div>
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-purple-500" />
                Conti Aziendali
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Conti creati automaticamente per ogni azienda
              </p>
              {contiAziende.length === 0 ? (
                <Card className="border-dashed border-2 border-slate-200">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                      <Building className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500">Nessun conto aziendale</p>
                    <p className="text-xs text-slate-400 mt-1">I conti vengono creati automaticamente con le aziende</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {contiAziende.map(conto => renderContoCard(conto, 'azienda'))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Tab Conti Partite IVA */}
          <TabsContent value="partite_iva">
            <div>
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-emerald-500" />
                Conti Partite IVA
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Conti per liberi professionisti e attività private
              </p>
              {contiPartiteIVA.length === 0 ? (
                <Card className="border-dashed border-2 border-slate-200">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                      <Briefcase className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500">Nessun conto partita IVA</p>
                    <p className="text-xs text-slate-400 mt-1">Crea partite IVA dalla sezione dedicata</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {contiPartiteIVA.map(conto => renderContoCard(conto, 'partita_iva'))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Dialog Movimento */}
      <Dialog open={movimentoDialogOpen} onOpenChange={setMovimentoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registra Movimento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMovimento} className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-xl">
              <p className="text-sm text-slate-500">Conto</p>
              <p className="font-semibold text-slate-800">{selectedConto?.titolare_nome}</p>
              <p className="text-sm text-slate-500">Saldo: <span className="text-amber-600 font-medium">{formatCurrency(selectedConto?.saldo || 0)}</span></p>
            </div>
            <div className="space-y-2">
              <Label>Tipo Operazione</Label>
              <select
                className="w-full border border-slate-200 rounded-xl p-3"
                value={movimentoForm.tipo}
                onChange={(e) => setMovimentoForm({...movimentoForm, tipo: e.target.value})}
              >
                <option value="DEPOSITO">Deposito / Accredito</option>
                <option value="PRELIEVO">Prelievo / Addebito</option>
                <option value="BONIFICO">Bonifico in Uscita</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Importo (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={movimentoForm.importo}
                onChange={(e) => setMovimentoForm({...movimentoForm, importo: e.target.value})}
                className="rounded-xl"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Causale</Label>
              <Input
                value={movimentoForm.causale}
                onChange={(e) => setMovimentoForm({...movimentoForm, causale: e.target.value})}
                placeholder="es. Stipendio, Pagamento bolletta..."
                className="rounded-xl"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setMovimentoDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-amber-500 hover:bg-amber-600">Conferma</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Banca;
